import React, { useEffect, useMemo, useState } from 'react';

import { ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import { isConfigInheritedForMerchant } from '../../../util/configInheritance';
import { inheritableLabelsFromConfigNames } from '../../../util/parentSettingSave';
import { ConfigResponse } from '../../../store/modules/merchant/types';
import { useMerchant } from '../../../contexts/MerchantContext';

type Props = {
  readOnly?: boolean;
  isSaving?: boolean;
  onSave: (disallowCartEdits: boolean) => Promise<void>;
  disallowCartEditsConfig?: ConfigResponse<boolean>;
};

export const CartEditsContent = ({
  onSave,
  disallowCartEditsConfig,
  readOnly = false,
  isSaving = false,
}: Props) => {
  const { merchantId } = useMerchant();
  const settingInherited = isConfigInheritedForMerchant(merchantId, disallowCartEditsConfig);
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
    isChildInheritedSaveEnabled,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);

  const serverAllowCartEdits = !disallowCartEditsConfig?.value;
  const [allowCartEdits, setAllowCartEdits] = useState(serverAllowCartEdits);

  useEffect(() => {
    setAllowCartEdits(serverAllowCartEdits);
    resetCustomizing();
  }, [serverAllowCartEdits, disallowCartEditsConfig?.configId, resetCustomizing]);

  const isFormDirty = allowCartEdits !== serverAllowCartEdits;
  const isSaveBarOpen = isChildInheritedSaveBarOpen(isFormDirty);

  const resetValues = () => {
    setAllowCartEdits(serverAllowCartEdits);
    resetCustomizing();
  };

  const handleSave = async () => {
    await onSave(!allowCartEdits);
    resetCustomizing();
  };

  const pageInheritableLabels = useMemo(
    () => inheritableLabelsFromConfigNames(['DISALLOW_CART_EDITS']),
    [],
  );
  const saveBarInheritance = getSaveBarInheritanceProps(
    isSaveBarOpen,
    isFormDirty ? pageInheritableLabels : [],
    pageInheritableLabels,
  );

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      <ConfigSwitchSection
        title="Cart Edits"
        description="Enable/Disable Cart Edits"
        isChecked={allowCartEdits}
        onToggle={() => setAllowCartEdits(!allowCartEdits)}
        disabled={controlsDisabled}
        plural
        switchTestId="CART_EDITS_SWITCH"
      />
      <ParentAwareSaveBar
        disableSave={readOnly || isSaving || !isChildInheritedSaveEnabled(isFormDirty)}
        saveLoading={isSaving}
        show={isSaveBarOpen && !readOnly}
        onCancel={() => resetValues()}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarInheritance.detailTextOverride}
        onSave={handleSave}
      />
    </>
  );
};
